import { useState } from 'react'
import { MapPin, User } from 'lucide-react'
import type { LiveWorkStatus } from '../services/liveWorkService'
import { formatWorkLocation } from '../services/liveWorkService'
import { TapeReels } from './TapeReels'
import { TapeFallbackBackground } from './TapeFallbackBackground'
import breakBg from '../assets/carpe-diem/tape-break.png'
import distractionBg from '../assets/carpe-diem/tape-distraction.png'

interface LiveWorkCardProps {
  status: LiveWorkStatus
  /** Shared daily thanks splash — used for Done for today. */
  thanksSplashUrl?: string | null
}

/** IEC 60094 Compact Cassette: 100.4mm × 63.8mm */
const CASSETTE_ASPECT = 'aspect-[1004/638]'

type FaceMode = 'playing' | 'locking-in' | 'break' | 'distraction' | 'idle' | 'sick' | 'done'

function faceMode(status: LiveWorkStatus): FaceMode {
  if (status.sickLeaveToday) return 'sick'
  if (!status.hasTimesheet || !status.screen) {
    return status.lockedOutToday ? 'done' : 'idle'
  }
  if (status.screen === 'locked-in') return 'playing'
  if (status.screen === 'locking-in') return 'locking-in'
  if (status.screen === 'break') return 'break'
  if (status.screen === 'distraction') return 'distraction'
  if (
    status.lockedOutToday ||
    status.screen === 'lock-out' ||
    status.screen === 'lock-out-planning' ||
    status.screen === 'thanks'
  ) {
    return 'done'
  }
  return 'idle'
}

function statusCaption(mode: FaceMode): string {
  switch (mode) {
    case 'playing':
      return 'Playing'
    case 'locking-in':
      return 'Locking in'
    case 'break':
      return 'On break'
    case 'distraction':
      return 'Distracted'
    case 'sick':
      return 'Leave - Absent Today'
    case 'done':
      return 'Done for today'
    default:
      return 'Not started'
  }
}

function taskLine(status: LiveWorkStatus, mode: FaceMode): string {
  if (mode === 'sick') return 'Leave - Absent Today'
  if (mode === 'break') return status.currentTask || 'Break'
  if (mode === 'distraction') return status.currentTask || 'Distraction'
  if (mode === 'done') return 'Day complete'
  if (mode === 'idle') return 'No live session yet'
  if (mode === 'locking-in') return status.currentTask || 'Choosing next task…'
  return status.currentTask || 'Locked in'
}

/** Translucent grey plate behind labels — same on every face. */
const TEXT_PLATE = 'bg-black/40 rounded px-1.5 py-0.5'

function PersonChip({ status }: { status: LiveWorkStatus }) {
  const [imgFailed, setImgFailed] = useState(false)
  const showPhoto = Boolean(status.avatar) && !imgFailed

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {showPhoto ? (
        <img
          src={status.avatar!}
          alt=""
          className="w-5 h-5 rounded-full object-cover flex-shrink-0 ring-1 ring-white/30"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="w-5 h-5 rounded-full flex-shrink-0 ring-1 ring-white/30 bg-white/20 flex items-center justify-center">
          <User className="w-3 h-3 text-white" aria-hidden />
        </span>
      )}
      <span className="text-[13px] font-semibold text-white truncate">
        {status.name}
      </span>
    </div>
  )
}

function TopInfoPlate({
  status,
  mode,
  location,
}: {
  status: LiveWorkStatus
  mode: FaceMode
  location: string
}) {
  return (
    <div className={`${TEXT_PLATE} flex flex-col gap-0.5 min-w-0`}>
      <div className="flex items-start justify-between gap-2">
        <PersonChip status={status} />
        <span className="text-[10px] uppercase tracking-wider text-white/90 flex-shrink-0 pt-0.5">
          {statusCaption(mode)}
        </span>
      </div>
      <div className="flex items-center gap-1 truncate text-white/90">
        <MapPin className="w-3 h-3 flex-shrink-0 opacity-90" />
        <span className="text-[12px] truncate">{location}</span>
      </div>
    </div>
  )
}

function TapeDeckFace({
  status,
  spinning,
  backgroundUrl = null,
}: {
  status: LiveWorkStatus
  spinning: boolean
  /** Override background (break / distraction dashboard art). */
  backgroundUrl?: string | null
}) {
  const location = formatWorkLocation(status.location)
  const mode = faceMode(status)
  const task = taskLine(status, mode)
  const tapeBg =
    backgroundUrl ?? (spinning ? status.tapeBackgroundUrl : null)
  const useFallback = !tapeBg && (spinning || mode === 'locking-in')

  return (
    <div
      className={`absolute inset-0 flex flex-col text-white overflow-hidden ${
        spinning && !backgroundUrl
          ? 'bg-gradient-to-b from-gray-700 to-gray-900'
          : backgroundUrl
            ? 'bg-gradient-to-b from-gray-700 to-gray-900'
            : 'bg-gradient-to-b from-gray-400 to-gray-500'
      }`}
    >
      {tapeBg ? (
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${tapeBg})` }}
          aria-hidden
        />
      ) : useFallback ? (
        <TapeFallbackBackground label={task} />
      ) : null}

      <div className="relative z-10 flex h-full flex-col px-2.5 py-2">
        <TopInfoPlate status={status} mode={mode} location={location} />

        <div className="relative flex-1 flex items-center min-h-0 my-1">
          <TapeReels spinning={spinning} />
        </div>

        <p
          className={`mt-auto text-[13px] font-medium leading-snug truncate text-white ${TEXT_PLATE} ${
            useFallback ? 'invisible' : ''
          }`}
          title={task}
        >
          {task}
        </p>
      </div>
    </div>
  )
}

function IdleFace({ status, mode }: { status: LiveWorkStatus; mode: FaceMode }) {
  const location = formatWorkLocation(status.location)
  const task = taskLine(status, mode)

  return (
    <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-gray-400 to-gray-500 text-white px-2.5 py-2">
      <TopInfoPlate status={status} mode={mode} location={location} />
      <div className="relative flex-1 flex items-center min-h-0 my-1 opacity-40">
        <TapeReels spinning={false} />
      </div>
      <p
        className={`mt-auto text-[13px] font-medium leading-snug text-white truncate ${TEXT_PLATE}`}
        title={task}
      >
        {task}
      </p>
    </div>
  )
}

export function LiveWorkCard({ status, thanksSplashUrl = null }: LiveWorkCardProps) {
  const mode = faceMode(status)

  return (
    <div
      className={`${CASSETTE_ASPECT} w-full relative rounded-xl overflow-hidden shadow-md ring-1 ring-black/10`}
      title={`${status.name} — ${statusCaption(mode)}`}
    >
      {/* Cassette shell edge */}
      <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-white/10 pointer-events-none z-20" />

      {mode === 'playing' && <TapeDeckFace status={status} spinning />}
      {mode === 'locking-in' && <TapeDeckFace status={status} spinning={false} />}
      {mode === 'break' && (
        <TapeDeckFace
          status={status}
          spinning={false}
          backgroundUrl={breakBg}
        />
      )}
      {mode === 'distraction' && (
        <TapeDeckFace
          status={status}
          spinning={false}
          backgroundUrl={distractionBg}
        />
      )}
      {mode === 'done' && (
        <TapeDeckFace
          status={status}
          spinning={false}
          backgroundUrl={thanksSplashUrl}
        />
      )}
      {(mode === 'idle' || mode === 'sick') && (
        <IdleFace status={status} mode={mode} />
      )}
    </div>
  )
}
